"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import { MdBackspace } from "react-icons/md";
import styles from "./css/refundAuthPinDialog.module.css";

const DIGITS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"];

export default function RefundAuthPinDialog({ open, onClose, onSubmit, codeLength = 4, inline = false }) {
  const resolvedLen = useMemo(() => (Number(codeLength) > 0 ? Number(codeLength) : 4), [codeLength]);
  const [code, setCode] = useState("");
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const attemptRef = useRef(0);

  useEffect(() => {
    if (!open) {
      attemptRef.current += 1;
      setCode("");
      setStatus("idle");
      setError("");
      return;
    }
    attemptRef.current += 1;
    setCode("");
    setStatus("idle");
    setError("");
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (status !== "idle") return;
    if (code.length !== resolvedLen) return;

    const attemptId = attemptRef.current + 1;
    attemptRef.current = attemptId;

    setStatus("loading");
    setError("");

    (async () => {
      try {
        const result = await (onSubmit ? onSubmit(code) : null);
        if (attemptRef.current !== attemptId) return;
        if (!open) return;

        if (result?.ok) {
          setStatus("success");
          const t = setTimeout(() => {
            if (attemptRef.current !== attemptId) return;
            if (onClose) onClose();
          }, 250);
          return () => clearTimeout(t);
        }

        setStatus("error");
        setError(result?.message || "Not authorized");
        const t = setTimeout(() => {
          if (attemptRef.current !== attemptId) return;
          setCode("");
          setStatus("idle");
          setError("");
        }, 900);
        return () => clearTimeout(t);
      } catch (e) {
        if (attemptRef.current !== attemptId) return;
        if (!open) return;

        setStatus("error");
        setError(e?.message || "Not authorized");
        const t = setTimeout(() => {
          if (attemptRef.current !== attemptId) return;
          setCode("");
          setStatus("idle");
          setError("");
        }, 900);
        return () => clearTimeout(t);
      }
    })();
  }, [code, resolvedLen, open, status, onSubmit, onClose]);

  const press = (d) => {
    if (status !== "idle") return;
    if (code.length < resolvedLen) setCode((c) => c + d);
  };

  const backspace = () => {
    if (status !== "idle") return;
    setCode((c) => c.slice(0, -1));
  };

  const clear = () => {
    if (status !== "idle") return;
    setCode("");
  };

  if (!open) return null;

  const content = (
    <div className={styles.panel}>
      <div className={styles.title}>Enter authorization code</div>

      <div className={styles.dots} aria-label="code progress">
        {Array.from({ length: resolvedLen }).map((_, i) => (
          <div key={i} className={i < code.length ? styles.dotFilled : styles.dot} />
        ))}
      </div>

      <div className={styles.keypad}>
        {DIGITS.slice(0, 3).map((d) => (
          <button key={d} className={styles.key} onClick={() => press(d)}>
            {d}
          </button>
        ))}
        {DIGITS.slice(3, 6).map((d) => (
          <button key={d} className={styles.key} onClick={() => press(d)}>
            {d}
          </button>
        ))}
        {DIGITS.slice(6, 9).map((d) => (
          <button key={d} className={styles.key} onClick={() => press(d)}>
            {d}
          </button>
        ))}
        <div className={styles.keypadSpacer}></div>
        <button className={styles.key} onClick={() => press("0")}>
          0
        </button>
        <div className={styles.keypadSpacer}></div>
      </div>

      <div className={styles.actions}>
        <button className={styles.actionBtn} onClick={clear} disabled={status !== "idle"}>
          Clear
        </button>
        <button className={styles.actionBtn} onClick={backspace} title="Backspace" aria-label="Backspace" disabled={status !== "idle"}>
          <MdBackspace size={20} color="#ef4444" />
        </button>
      </div>

      {status === "loading" ? <div className={styles.loading}>Checking...</div> : null}
      {status === "success" ? <div className={styles.success}>Authorized</div> : null}
      {status === "error" ? <div className={styles.error}>{error || "Not authorized"}</div> : null}

      <div className={styles.footer}>
        <button className={styles.cancelBtn} onClick={() => (onClose ? onClose() : null)}>
          Cancel
        </button>
      </div>
    </div>
  );

  if (inline) {
    return (
      <div className={styles.inlineWrap} role="dialog" aria-modal="false">
        <div className={styles.inlineModal}>{content}</div>
      </div>
    );
  }

  return (
    <div className={styles.overlay} role="dialog" aria-modal="true">
      <div className={styles.modal}>
        {content}
      </div>
    </div>
  );
}
